import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Building2 } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { SetuMark } from "@/components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill Name, Email and Message");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error("Failed");
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="contact-logo">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
            <span className="font-heading text-lg tracking-tight">Setubuild.OS</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher /><ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12">
        <div className="max-w-2xl">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Get in Touch</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.1] sm:leading-tight">We're here to help</h1>
          <p className="text-muted-foreground mt-3 sm:mt-4 text-sm sm:text-base">
            Questions about pricing, features, or your project? Reach out — we respond within 24 hours on business days.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12">
          {/* Contact Info Cards */}
          <div className="md:col-span-1 space-y-4">
            <ContactCard
              icon={<Phone className="w-4 h-4" />}
              label="Phone"
              value="+91 97667 44746"
              href="tel:+919766744746"
              testid="contact-phone"
            />
            <ContactCard
              icon={<Mail className="w-4 h-4" />}
              label="Email"
              value="setubuildtechnologies@gmail.com"
              href="mailto:setubuildtechnologies@gmail.com"
              testid="contact-email"
            />
            <ContactCard
              icon={<MessageSquare className="w-4 h-4" />}
              label="WhatsApp"
              value="+91 97667 44746"
              href="https://wa.me/919766744746"
              testid="contact-whatsapp"
            />
            <ContactCard
              icon={<Clock className="w-4 h-4" />}
              label="Business Hours"
              value="Mon – Sat · 10 AM – 7 PM IST"
              testid="contact-hours"
            />
            <ContactCard
              icon={<Building2 className="w-4 h-4" />}
              label="Company"
              value="SetuBuild Technologies"
              subvalue="Bharat, India"
              testid="contact-company"
            />
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="md:col-span-2 border border-border rounded-lg p-5 sm:p-6 md:p-8 bg-card" data-testid="contact-form">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Send us a message</div>
            <h2 className="font-heading text-xl sm:text-2xl mb-4 sm:mb-6">Tell us what you need</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name *" testid="contact-form-name">
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Your name" data-testid="contact-name-input" />
              </Field>
              <Field label="Email *" testid="contact-form-email">
                <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Email" data-testid="contact-email-input" />
              </Field>
              <Field label="Phone (optional)" testid="contact-form-phone">
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} type="tel" inputMode="tel" className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Phone" data-testid="contact-phone-input" />
              </Field>
              <Field label="Subject" testid="contact-form-subject">
                <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="contact-subject-input">
                  <option value="">Select…</option>
                  <option value="sales">Sales & Pricing</option>
                  <option value="support">Product Support</option>
                  <option value="billing">Billing & Refunds</option>
                  <option value="partnership">Partnership</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>

            <Field label="Message *" testid="contact-form-message">
              <textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" aria-label="Message" data-testid="contact-message-input" />
            </Field>

            <button type="submit" disabled={submitting} className="mt-2 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-3 sm:py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition" data-testid="contact-submit-btn">
              <Send className="w-4 h-4" />
              {submitting ? "Sending…" : "Send Message"}
            </button>

            <p className="text-xs text-muted-foreground mt-4">
              By submitting this form, you agree to our <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>. We'll never share your details.
            </p>
          </form>
        </div>

        {/* Quick Links */}
        <div className="mt-16 border-t border-border pt-10">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">Quick Links</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <QuickLink to="/features" title="Features" desc="Explore what Setubuild.OS can do" />
            <QuickLink to="/privacy" title="Privacy Policy" desc="How we protect your data" />
            <QuickLink to="/terms" title="Terms of Service" desc="Legal terms & conditions" />
            <QuickLink to="/refund" title="Refund Policy" desc="Refund & cancellation info" />
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">© {new Date().getFullYear()} SetuBuild Technologies. All rights reserved. · Bharat</div>
          <div className="flex gap-4 mono text-[10px] uppercase tracking-widest">
            <Link to="/privacy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" className="hover:text-primary">Terms</Link>
            <Link to="/refund" className="hover:text-primary">Refund</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const ContactCard = ({ icon, label, value, subvalue, href, testid }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="w-7 h-7 rounded-md bg-secondary grid place-items-center">{icon}</span>
        <span className="mono text-[10px] uppercase tracking-[0.2em]">{label}</span>
      </div>
      <div className="mt-2 text-sm font-medium">{value}</div>
      {subvalue && <div className="text-xs text-muted-foreground mt-0.5">{subvalue}</div>}
    </>
  );
  const cls = "block border border-border rounded-lg p-4 hover:border-primary/40 transition";
  return href ? (
    <a href={href} className={cls} data-testid={testid}>{inner}</a>
  ) : (
    <div className={cls} data-testid={testid}>{inner}</div>
  );
};

const Field = ({ label, children, testid }) => (
  <label className="block mt-4 first:mt-0 sm:mt-0" data-testid={testid}>
    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">{label}</div>
    {children}
  </label>
);

const QuickLink = ({ to, title, desc }) => (
  <Link to={to} className="block border border-border rounded-lg p-4 hover:border-primary/40 transition group" data-testid={`quicklink-${title.toLowerCase().replace(/ /g, "-")}`}>
    <div className="text-sm font-medium group-hover:text-primary transition">{title}</div>
    <div className="text-xs text-muted-foreground mt-1">{desc}</div>
  </Link>
);
